// src/components/character_editor/sections/BasicInfoEditSection.jsx
// Правки: поле background теперь строка для TextField.
// Объект легенды раскрывается на отдельные поля (title, lineage, ...).
// Каждое изменение уходит в onBackgroundChange уже объектом известной формы.

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';

import {
  normalizeLegend,
  legendToString,
} from '../../../utils/characterDraft';

const BasicInfoEditSection = ({
  character,
  onNameChange,
  onBackgroundChange,
}) => {
  const legend = useMemo(
    () => normalizeLegend(character?.background),
    [character?.background]
  );

  // Единая строка для основного TextField — но НЕ для хранения.
  // Меняем только через отдельные поля ниже.
  const backgroundText = useMemo(() => legendToString(legend), [legend]);

  // Патч одного поля легенды — уходит в onBackgroundChange объектом
  const patchLegend = (field, value) => {
    const next = normalizeLegend({ ...legend, [field]: value });
    if (onBackgroundChange) onBackgroundChange(next);
  };

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
      >
        <PersonIcon sx={{ mr: 2 }} />
        Основная информация
      </Typography>

      <Grid container spacing={3}>
        {/* Имя */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Имя персонажа</Typography>
              <TextField
                fullWidth
                label="Имя"
                value={character?.name || ''}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Введите имя персонажа..."
                helperText="Минимум 2 символа, максимум 50"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Уровень и опыт */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Прогресс</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 40, height: 40 }}>
                  {character?.level || 1}
                </Avatar>
                <Box>
                  <Typography variant="body1">Уровень {character?.level || 1}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Опыт: {character?.experience || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Легенда: поля по одному */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>История персонажа</Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Звание"
                    value={legend.title}
                    onChange={(e) => patchLegend('title', e.target.value)}
                    placeholder="Например: Лейтенант, Мастер-ремесленник..."
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Род"
                    value={legend.lineage}
                    onChange={(e) => patchLegend('lineage', e.target.value)}
                    placeholder="Семья, клан, династия..."
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Родина"
                    value={legend.birthplace}
                    onChange={(e) => patchLegend('birthplace', e.target.value)}
                    placeholder="Планета, станция, колония..."
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Память детства"
                    value={legend.childhood_memory}
                    onChange={(e) => patchLegend('childhood_memory', e.target.value)}
                    placeholder="Яркое воспоминание из детства..."
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Мотивация"
                    value={legend.motivation}
                    onChange={(e) => patchLegend('motivation', e.target.value)}
                    placeholder="Что движет персонажем..."
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Предыстория"
                    value={legend.background}
                    onChange={(e) => patchLegend('background', e.target.value)}
                    placeholder="Опишите историю персонажа, его прошлое, мотивации и цели..."
                    helperText="Это поможет лучше отыгрывать персонажа в игре"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Дополнительное описание"
                    value={legend.description}
                    onChange={(e) => patchLegend('description', e.target.value)}
                    placeholder="Краткое описание для карточки персонажа..."
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary" display="block">
                Сводный просмотр:
              </Typography>
              <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="body2" color="text.secondary" whiteSpace="pre-wrap">
                  {backgroundText || 'Предыстория пока пуста'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Метаданные */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ bgcolor: 'background.default' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Информация о создании</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Создан</Typography>
                  <Typography variant="body1">
                    {character?.created_at
                      ? new Date(character.created_at).toLocaleDateString('ru-RU')
                      : 'Неизвестно'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Обновлен</Typography>
                  <Typography variant="body1">
                    {character?.updated_at
                      ? new Date(character.updated_at).toLocaleDateString('ru-RU')
                      : 'Никогда'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">ID персонажа</Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {character?.id || 'Не задан'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInfoEditSection;
